import { useEffect, useMemo, useState, useRef } from 'preact/hooks'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { useOption } from 'libs/athos/hooks/useOption'

/**
 * Returns only the current page's results (no cross-page accumulation).
 * Used for numbered pagination, where each page replaces the grid contents
 * rather than appending to it.
 */
export function usePagedResults() {
  const { results, options, isLoading: fetchLoading, refine } = useAthosContext()

  const [merchandisingBanners, setMerchandisingBanners] = useState([])

  useEffect(() => {
    if (results?.merchandising?.content) {
      setMerchandisingBanners({
        header: results.merchandising.content.header || [],
        footer: results.merchandising.content.footer || [],
        banner: results.merchandising.content.banner || []
      })
    }
  }, [results?.merchandising?.content])

  // Keep the page option synced to the URL (?page=)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pageOption = useOption('page', { defaultValue: 1, syncWithURL: true })

  // Latch the loading flag so skeletons show for the whole fetch, not just the
  // first tick, which keeps the skeleton-to-results transition smooth.
  const hasBeenLoading = useRef(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isLoading && hasBeenLoading.current) {
      hasBeenLoading.current = false
    }

    if (hasBeenLoading.current) {
      setIsLoading(fetchLoading)
      return
    }

    if (fetchLoading) {
      hasBeenLoading.current = true
      setIsLoading(true)
    }
  }, [fetchLoading])

  const pageResults = useMemo(() => {
    if (!results?.results) return []
    const items = [...results.results]

    const startIndex = results?.pagination?.begin
    const endIndex = results?.pagination?.end
    const currentPage = results?.pagination?.currentPage

    // Splice inline merchandising into the current page using a page-relative
    // index so banners land correctly on pages beyond the first.
    results?.merchandising?.content?.inline?.forEach((content) => {
      const index = content.config.position.index
      if (index > endIndex || index < startIndex) return
      items.splice(index - startIndex, 0, content)
    })

    items.forEach((item) => (item.page = currentPage))
    return items
  }, [results])

  return { pageResults, merchandisingBanners, isLoading, options, results, refine }
}
